import { DecimalPipe } from "@angular/common";
import { Component, effect, inject, input, type OnDestroy, signal } from "@angular/core";
import { FormsModule } from "@angular/forms";
import type { ColumnMeta, Corpus } from "@pienso/ingest-types/corpus.interface";
import { AlertService, Button, ButtonIcon, Icon, Select, TabBar, TabItem, TextInput, Toggle } from "@ui";
import { distinctUntilChanged, type Subscription, skip } from "rxjs";
import { TRANSFORMER_STATE } from "../abstract-transformer.service";
import type { TransformerToolbar } from "../toolbar.interface";
import { TransformerService } from "../transformer.service";
import { TransformerGridDataService } from "../transformer-grid-data.service";
import { FIND_REPLACE_ACTIONS, FindReplaceService } from "./find-replace.service";

interface ColumnOption {
  id: string;
  name: string;
}

type FindReplaceMode = "replace" | "remove";

@Component({
  selector: "app-findreplace-toolbar",
  templateUrl: "./find-replace-toolbar.component.html",
  styleUrls: ["./find-replace-toolbar.component.scss"],
  imports: [FormsModule, DecimalPipe, Button, ButtonIcon, Icon, Select, TabBar, TabItem, TextInput, Toggle],
})
export class FindReplaceToolbarComponent implements TransformerToolbar, OnDestroy {
  private service = inject(FindReplaceService);
  private dataService = inject(TransformerGridDataService);
  private transformerService = inject(TransformerService);
  private alertService = inject(AlertService);

  readonly corpus = input.required<Corpus>();
  #columnOptions: ColumnOption[] | null = null;

  protected readonly mode = signal<FindReplaceMode>("remove");
  protected readonly removeEntireRow = signal(false);
  protected readonly matchCount = signal(0);

  private hasData = false;
  private subscription: Subscription | null = null;

  // Cached so option identities stay stable; a new array each call would retrigger ngModel every cycle.
  get columnOptions(): ColumnOption[] {
    if (!this.#columnOptions) {
      const meta = this.corpus().columnMeta as ColumnMeta;
      this.#columnOptions = (meta.columnOrder ?? []).map((id) => ({ id, name: meta.columns[id]?.name ?? id }));
    }
    return this.#columnOptions;
  }

  constructor() {
    // corpus can be reassigned to a new object (e.g. after a save); recompute options when it changes.
    effect(() => {
      this.corpus();
      this.#columnOptions = null;
    });

    this.subscription = this.dataService.gridData$.subscribe((data) => {
      this.hasData = !!data?.page.totalRows;
      this.matchCount.set(data?.page.availableRows ?? 0);
    });

    // skip(1) drops the replayed current state, which is already DONE when editing an existing transform.
    this.subscription.add(
      this.service.state$.pipe(skip(1), distinctUntilChanged()).subscribe((state) => {
        if (state === TRANSFORMER_STATE.ERROR) {
          this.alertService.error("Problem saving changes.");
        } else if (state === TRANSFORMER_STATE.DONE) {
          this.alertService.success("Changes saved.");
        }
      }),
    );
  }

  ngOnDestroy() {
    this.subscription?.unsubscribe();
    this.subscription = null;
  }

  selectedColumn(): ColumnOption | null {
    return this.columnOptions.find((option) => option.id === this.service.columnId) ?? null;
  }

  // p-select echoes its value through ngModelChange on init; re-emitting would reload the grid and loop.
  onColumnChange(option: ColumnOption | null): void {
    const id = option?.id ?? null;
    if (id !== this.service.columnId) {
      this.service.columnId = id;
    }
  }

  findText(): string {
    return this.service.findText;
  }

  replaceText(): string {
    return this.service.replaceText;
  }

  onFindChange(value: string): void {
    if (value !== this.service.findText) {
      this.service.findText = value;
    }
  }

  onReplaceChange(value: string): void {
    if (value !== this.service.replaceText) {
      this.service.replaceText = value;
    }
  }

  onModeChange(mode: string | undefined): void {
    this.mode.set(mode === "replace" ? "replace" : "remove");
  }

  hasMatches(): boolean {
    return !!this.service.columnId && !!this.service.findText && this.matchCount() > 0;
  }

  get isPending(): boolean {
    return this.service.state === TRANSFORMER_STATE.PENDING;
  }

  canApply(): boolean {
    return this.hasData && this.hasMatches() && this.service.state === TRANSFORMER_STATE.EDITING;
  }

  apply(): void {
    if (this.mode() === "replace") {
      this.service.doAction(FIND_REPLACE_ACTIONS.REPLACE);
      return;
    }
    this.service.replaceText = "";
    this.service.doAction(this.removeEntireRow() ? FIND_REPLACE_ACTIONS.DELETE_ROWS : FIND_REPLACE_ACTIONS.REMOVE);
  }

  // TRANSFORMER_ACTION.CANCEL only rejects the pending transform promise; this is what closes the toolbar.
  cancel(): void {
    this.transformerService.cancel();
  }
}
